// ============================================================
// PC 端布局 — 深色科技风
// ============================================================

import { useEffect } from 'react'
import { Layout, Menu, Badge, Dropdown, Button } from 'antd'
import UserAvatar from '../components/UserAvatar'
import {
  HomeOutlined,
  ShopOutlined,
  UserOutlined,
  BellOutlined,
  LogoutOutlined,
  WalletOutlined,
  FileTextOutlined,
  StarOutlined,
  LoginOutlined,
  GiftOutlined,
  TeamOutlined,
  DashboardOutlined,
  DownOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import type { MenuProps } from 'antd'
import { useAuthStore } from '../stores/auth.store'
import { useNotificationStore } from '../stores/notification.store'
import { formatPoints } from '@task-bazaar/shared'
import { triggerLoginModal } from '../components/LoginModal'

const { Header, Content } = Layout

const navItems: MenuProps['items'] = [
  { key: '/', icon: <HomeOutlined />, label: '任务广场' },
  { key: '/shop', icon: <ShopOutlined />, label: '积分商城' },
]

const getUserMenuItems = (isAdmin: boolean): MenuProps['items'] => [
  { key: '/my', icon: <UserOutlined />, label: '个人中心' },
  { key: '/my/published', icon: <FileTextOutlined />, label: '我发布的' },
  { key: '/my/accepted', icon: <StarOutlined />, label: '我接取的' },
  { key: '/my/points', icon: <WalletOutlined />, label: '积分明细' },
  { key: '/my/invite', icon: <TeamOutlined />, label: '邀请好友' },
  ...(isAdmin ? [
    { type: 'divider' as const },
    { key: '/admin', icon: <DashboardOutlined />, label: '后台管理' },
  ] : []),
  { type: 'divider' },
  { key: 'logout', icon: <LogoutOutlined />, label: '退出登录' },
]

export default function PCLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isLoggedIn, user, logout } = useAuthStore()
  const { unreadCount, startPolling, stopPolling } = useNotificationStore()

  // 登录后启动通知轮询
  useEffect(() => {
    if (isLoggedIn) {
      startPolling()
    } else {
      stopPolling()
    }
    return () => stopPolling()
  }, [isLoggedIn])

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      logout()
    } else {
      navigate(key)
    }
  }

  return (
    <Layout style={{ minHeight: '100vh', background: 'transparent' }}>
      {/* ─── Header ─── */}
      <Header
        style={{
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          padding: '0 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          height: 64,
        }}
      >
        {/* 左侧：Logo + 导航 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 32, flex: 1, minWidth: 0 }}>
          {/* Logo */}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flexShrink: 0 }}
            onClick={() => navigate('/')}
          >
            <img
              src="/logo.svg"
              alt="广发圩"
              style={{ width: 36, height: 36, borderRadius: 8 }}
            />
            <span
              style={{
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: 16,
                letterSpacing: '-0.02em',
              }}
            >
              广发圩
            </span>
          </div>

          {/* 顶部导航菜单 */}
          <Menu
            mode="horizontal"
            selectedKeys={[location.pathname]}
            items={navItems}
            onClick={onMenuClick}
            style={{
              background: 'transparent',
              border: 'none',
              lineHeight: '62px',
              flex: 1,
              minWidth: 0,
            }}
            theme="dark"
          />
        </div>

        {/* 右侧：积分 + 通知 + 用户 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {isLoggedIn ? (
            <>
              {/* 积分展示 */}
              <div
                onClick={() => navigate('/my/points')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  borderRadius: 8,
                  background: 'rgba(245, 158, 11, 0.1)',
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  height: 32,
                }}
                className="hover:!bg-[rgba(245,158,11,0.15)]"
              >
                <GiftOutlined style={{ color: '#F59E0B', fontSize: 14 }} />
                <span style={{ color: '#F59E0B', fontSize: 13, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>
                  {formatPoints(user?.balance || 0)}
                </span>
              </div>

              {/* 通知铃铛 */}
              <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                <button
                  onClick={() => navigate('/notifications')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: 10,
                    width: 38,
                    height: 38,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#A0A0B0',
                    transition: 'all 0.2s',
                  }}
                  className="hover:!border-[rgba(255,255,255,0.15)] hover:!text-[#fff]"
                >
                  <BellOutlined />
                </button>
              </Badge>

              {/* 用户下拉菜单 */}
              <Dropdown menu={{ items: getUserMenuItems(user?.role === 'admin'), onClick: onMenuClick }} placement="bottomRight">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    padding: '6px 12px',
                    borderRadius: 10,
                    transition: 'background 0.2s',
                  }}
                  className="hover:!bg-[rgba(255,255,255,0.05)]"
                >
                  <UserAvatar avatar={user?.avatar} size={30} />
                  <span style={{ color: '#A0A0B0', fontSize: 13, fontWeight: 500 }}>
                    {user?.nickname || '用户'}
                  </span>
                  <DownOutlined style={{ color: '#6B6B80', fontSize: 10 }} />
                </div>
              </Dropdown>
            </>
          ) : (
            /* 未登录：显示注册/登录按钮 */
            <Button
              type="primary"
              icon={<LoginOutlined />}
              onClick={() => triggerLoginModal()}
              style={{
                background: 'linear-gradient(135deg, #8B5CF6, #EC4899)',
                border: 'none',
                borderRadius: 10,
                height: 38,
                padding: '0 16px',
                fontWeight: 500,
              }}
            >
              注册/登录
            </Button>
          )}
        </div>
      </Header>

      {/* ─── Content ─── */}
      <Content style={{ padding: '0', background: 'transparent', minHeight: 'calc(100vh - 64px)' }}>
        <Outlet />
      </Content>
    </Layout>
  )
}
